
import StatCard from './components/StatCard';
import RecentLoans from './components/RecentLoans';
import CategoryChart from './components/CategoryChart';
import RevenueChart from './components/RevenueChart';
import { Link } from 'react-router-dom';
import { useLoansStore, useInventoryStore, useCustomersStore, useRevenueStore } from '@/hooks/useSupabaseStore';
import { useCapital } from '@/hooks/useCapital';

export default function DashboardPage() {
  const { loans } = useLoansStore();
  const { items } = useInventoryStore();
  const { customers } = useCustomersStore();
  const { currentMonthRevenue, logs: revenueLogs } = useRevenueStore();

  const activeLoans = loans.filter(l => l.status !== 'redeemed' && l.status !== 'forfeited').length;
  const now = new Date();
  const currentMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const newCustomers = customers.filter(c => c.createdAt.startsWith(currentMonthStr)).length;
  const monthRevenue = currentMonthRevenue;

  const { totalCapital } = useCapital();
  const totalActiveLoanAmount = loans
    .filter(l => l.status !== 'redeemed' && l.status !== 'forfeited')
    .reduce((s, l) => s + l.loanAmount, 0);
  const remainingCapital = Math.max(0, totalCapital - totalActiveLoanAmount);

  // Doanh thu ước tính = tổng lãi dự kiến từ tất cả khoản vay đang active/overdue/due_soon
  const estimatedRevenue = loans
    .filter(l => l.status === 'active' || l.status === 'overdue' || l.status === 'due_soon')
    .reduce((sum, loan) => {
      const startD = new Date(loan.startDate);
      const dueD = new Date(loan.dueDate);
      const days = Math.max(1, Math.ceil((dueD.getTime() - startD.getTime()) / (1000 * 60 * 60 * 24)));
      const interest = Math.round(loan.loanAmount * loan.interestRate / 100 / 30 * days);
      return sum + interest;
    }, 0);

  // ── THÔNG BÁO CẢNH BÁO ──
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const alertLoans = loans
    .filter(l => l.status !== 'redeemed' && l.status !== 'forfeited')
    .map(l => {
      const due = new Date(l.dueDate);
      due.setHours(0, 0, 0, 0);
      const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return { ...l, daysLeft: diff };
    })
    .filter(l => l.daysLeft <= 7)
    .sort((a, b) => a.daysLeft - b.daysLeft);

  const overdueCount = alertLoans.filter(l => l.daysLeft < 0).length;
  const dueSoonCount = alertLoans.filter(l => l.daysLeft >= 0).length;

  // ── ACTIVITY LOGS ĐỘNG ──
  const activityLogs = [
    ...loans.slice(0, 4).map(loan => ({
      id: `loan-${loan.id}`,
      icon: loan.status === 'active' ? 'ri-file-add-line' : loan.status === 'redeemed' ? 'ri-checkbox-circle-line' : loan.status === 'forfeited' ? 'ri-alarm-warning-line' : 'ri-file-list-3-line',
      color: loan.status === 'active' ? 'bg-blue-100 text-blue-600' : loan.status === 'redeemed' ? 'bg-emerald-100 text-emerald-600' : loan.status === 'forfeited' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600',
      description: `${loan.customerName} — ${loan.assetName} (${loan.id})`,
      amount: loan.loanAmount,
      time: `Ngày vay: ${loan.startDate.slice(0, 10)}`,
    })),
    ...revenueLogs.slice(0, 4).map(log => ({
      id: `rev-${log.id}`,
      icon: log.type === 'redeem' ? 'ri-money-dollar-circle-line' : 'ri-time-line',
      color: log.type === 'redeem' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600',
      description: `${log.type === 'redeem' ? 'Chuộc đồ' : 'Gia hạn'} — ${log.customerName} · ${log.assetName}`,
      amount: log.interestAmount,
      time: new Date(log.createdAt).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    })),
  ].slice(0, 6);

  return (<>
      {/* ── BANNER CẢNH BÁO ── */}
      {alertLoans.length > 0 && (
        <div className="mb-4 md:mb-6 space-y-2">
          {overdueCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 md:px-5 py-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-lg shrink-0">
                <i className="ri-alarm-warning-line text-red-500 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-red-700 text-sm font-semibold">
                  {overdueCount} khoản vay đã quá hạn
                </p>
                <p className="text-red-400 text-xs mt-0.5">
                  Cần liên hệ khách hàng để thu hồi hoặc gia hạn ngay
                </p>
              </div>
              <Link
                to="/loans"
                className="shrink-0 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Xem ngay
              </Link>
            </div>
          )}
          {dueSoonCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 md:px-5 py-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                <i className="ri-time-line text-amber-500 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-700 text-sm font-semibold">
                  {dueSoonCount} khoản vay sắp đến hạn (trong 7 ngày)
                </p>
                <p className="text-amber-500 text-xs mt-0.5">
                  Chuẩn bị nhắc nhở khách hàng đóng lãi hoặc chuộc đồ
                </p>
              </div>
              <Link
                to="/loans"
                className="shrink-0 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Xem ngay
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <StatCard title="Khoản Vay Đang Hoạt Động" value={String(activeLoans)} change="+14%" changeType="up" icon="ri-file-list-3-line" bgColor="bg-blue-50" iconColor="text-blue-600" />
        <StatCard title="Tổng Tiền Cho Vay" value={`${(items.filter(i => i.status === 'pawned').reduce((s, i) => s + i.loanAmount, 0) / 1000000).toFixed(1)}M đ`} change="+8%" changeType="up" icon="ri-archive-2-line" bgColor="bg-amber-50" iconColor="text-amber-600" />
        <StatCard title="Khách Hàng Mới Tháng Này" value={String(newCustomers)} change="+33%" changeType="up" icon="ri-contacts-book-2-line" bgColor="bg-purple-50" iconColor="text-purple-600" />
        <StatCard title={`Doanh Thu Thực Tháng ${now.getMonth() + 1}`} value={monthRevenue > 0 ? `${(monthRevenue / 1000000).toFixed(2)}M đ` : `0đ`} change={revenueLogs.filter(l => l.createdAt.startsWith(currentMonthStr)).length > 0 ? `+${revenueLogs.filter(l => l.createdAt.startsWith(currentMonthStr)).length} giao dịch` : 'Chưa có'} changeType="up" icon="ri-money-dollar-circle-line" bgColor="bg-emerald-50" iconColor="text-emerald-600" />
        <StatCard title="Tổng Vốn" value={totalCapital > 0 ? `${(totalCapital / 1000000).toFixed(1)}M đ` : '0đ'} change="Từ cài đặt" changeType="neutral" icon="ri-safe-2-line" bgColor="bg-gray-50" iconColor="text-gray-600" />
        <StatCard title="Vốn Còn Lại" value={remainingCapital > 0 ? `${(remainingCapital / 1000000).toFixed(1)}M đ` : '0đ'} change={`${totalCapital > 0 ? Math.round((remainingCapital / totalCapital) * 100) : 0}% khả dụng`} changeType="neutral" icon="ri-wallet-3-line" bgColor={remainingCapital > 0 ? 'bg-emerald-50' : 'bg-red-50'} iconColor={remainingCapital > 0 ? 'text-emerald-600' : 'text-red-600'} />
      </div>

      {/* Doanh thu ước tính */}
      <div className="bg-white rounded-2xl px-4 md:px-6 py-4 mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl shrink-0">
              <i className="ri-line-chart-line text-amber-600 text-lg"></i>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">Doanh Thu Ước Tính</p>
              <p className="text-xs text-gray-400">Tổng lãi dự kiến từ {loans.filter(l => ['active','overdue','due_soon'].includes(l.status)).length} khoản vay đang hoạt động</p>
            </div>
          </div>
          <div className="flex items-center gap-4 md:gap-8 flex-wrap w-full sm:w-auto">
            <div className="text-center flex-1 sm:flex-none">
              <p className="text-xs text-gray-400 mb-0.5">Tổng lãi ước tính</p>
              <p className="text-lg md:text-xl font-bold text-amber-600">{estimatedRevenue > 0 ? `${(estimatedRevenue / 1000000).toFixed(2)}M đ` : '0đ'}</p>
            </div>
            <div className="text-center flex-1 sm:flex-none">
              <p className="text-xs text-gray-400 mb-0.5">Đã thu thực tế</p>
              <p className="text-lg md:text-xl font-bold text-emerald-600">{monthRevenue > 0 ? `${(monthRevenue / 1000000).toFixed(2)}M đ` : '0đ'}</p>
            </div>
            <div className="text-center flex-1 sm:flex-none">
              <p className="text-xs text-gray-400 mb-0.5">Còn lại chưa thu</p>
              <p className="text-lg md:text-xl font-bold text-gray-700">
                {estimatedRevenue - monthRevenue > 0
                  ? `${((estimatedRevenue - monthRevenue) / 1000000).toFixed(2)}M đ`
                  : '0đ'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loans + Category */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4 md:mb-6">
        <div className="xl:col-span-2">
          <RecentLoans loans={loans} />
        </div>
        <div>
          <CategoryChart items={items} />
        </div>
      </div>

      {/* Revenue + Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <RevenueChart logs={revenueLogs} />
        </div>
        <div className="bg-white rounded-2xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 text-sm md:text-base">Hoạt Động Gần Đây</h3>
          </div>
          <div className="space-y-3">
            {activityLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${log.color}`}>
                  <i className={`${log.icon} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 text-xs leading-snug">{log.description}</p>
                  {log.amount !== undefined && log.amount > 0 && (
                    <p className="text-amber-600 text-xs font-semibold">{log.amount.toLocaleString('vi-VN')}đ</p>
                  )}
                  <p className="text-gray-400 text-[10px] mt-0.5">{log.time}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/activity" className="mt-4 block text-center text-xs text-[#f59e0b] font-medium hover:underline cursor-pointer">
            Xem tất cả hoạt động
          </Link>
        </div>
      </div>
    </>
  );
}
